import React, { useState } from 'react';
import {
  Mail,
  Phone,
  Menu as MenuIcon,
  X as CloseIcon,
  Facebook,
  Instagram,
  DollarSign,
  Snowflake,
  Sun,
  Building2,
} from 'lucide-react';
import { Link } from 'react-router-dom';

const TabButton = ({ icon: Icon, label, active, onClick }) => (
  <button
    onClick={onClick}
    className={`inline-flex items-center gap-2 px-4 py-2 rounded-full border transition
      ${active
        ? 'bg-orange-600 text-white border-orange-600 shadow'
        : 'bg-white text-orange-700 border-orange-200 hover:bg-orange-50'
      }`}
  >
    <Icon className="h-4 w-4" />
    <span className="font-semibold">{label}</span>
  </button>
);

const TrainingCard = ({ title, price }) => (
  <article className="bg-white rounded-2xl shadow-md p-5 border border-amber-100 hover:shadow-lg transition">
    <h3 className="text-base font-bold text-gray-900 leading-snug">{title}</h3>
    <p className="mt-2 text-2xl font-extrabold text-orange-600">{price}</p>
  </article>
);

const SimpleTable = ({ headers, rows }) => (
  <div className="overflow-x-auto rounded-lg border border-gray-300 bg-white">
    <table className="pricing-table w-full border-collapse">
      <thead>
        <tr className="bg-orange-100">
          {headers.map((header) => (
            <th key={header} scope="col">
              {header}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((row, rowIndex) => (
          <tr key={rowIndex} className={rowIndex % 2 === 1 ? 'bg-gray-50' : 'bg-white'}>
            {row.map((cell, cellIndex) => (
              <td key={cellIndex} className={cellIndex === 0 ? 'font-semibold' : ''}>
                {cell}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const Prising = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const toggleMenu = () => setIsMenuOpen((s) => !s);

  // --- TABS (bez INFO) ---
  const TABS = [
    { key: 'pronajemZima', label: 'Pronájem haly (zima)', icon: Building2 },
    { key: 'treninkyZima', label: 'Tréninky – zima', icon: Snowflake },
    { key: 'pronajemLeto', label: 'Pronájem kurtů – léto', icon: Sun },
    { key: 'treninkyLeto', label: 'Tréninky – léto', icon: Sun },
  ];
  const [tab, setTab] = useState('pronajemZima');

  return (
    <div className="min-h-screen bg-amber-50">
      {/* HEADER */}
      <header className="bg-gradient-to-r from-orange-500 to-red-600 text-white fixed top-5 left-4 right-4 z-50 px-6 py-2 rounded-2xl shadow-lg">
        <nav className="max-w-screen-xl mx-auto flex justify-between items-center">
          <div className="flex items-center">
            <Link to="/" aria-label="Domů">
              <img
                src={`${process.env.PUBLIC_URL}/logocimice.png`}
                alt="Tenis Čimice Logo"
                className="h-12 w-12 mr-2"
              />
            </Link>
            <div className="ml-2 flex flex-col items-center">
              <a
                href="https://www.facebook.com/people/Kate%C5%99ina-Peterkov%C3%A1/pfbid0TncMRnyejaJkEkYUzi36H7si3prwYeLDfqJiudBjHMHcPPrWKEeyokFt3Nctphj2l/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-white/90 hover:text-yellow-300 transition"
                aria-label="Facebook"
              >
                <Facebook className="h-5 w-5" />
              </a>
              <a
                href="https://www.instagram.com/tenis_cimice/?utm_source=qr"
                target="_blank"
                rel="noopener noreferrer"
                className="text-white/90 hover:text-yellow-300 mt-1 transition"
                aria-label="Instagram"
              >
                <Instagram className="h-5 w-5" />
              </a>
            </div>
          </div>

          <ul className="hidden md:flex md:space-x-4">
            {['O nás', 'Aktuality', 'Kempy', 'Ceník', 'Školička', 'Doplňkové služby', 'Kontakt'].map(
              (item) => (
                <li key={item} className="flex-shrink-0 relative group">
                  <Link
                    to={
                      item === 'O nás'
                        ? '/o-nas'
                        : item === 'Aktuality'
                        ? '/aktuality'
                        : item === 'Kempy'
                        ? '/kempy'
                        : item === 'Ceník'
                        ? '/cenik'
                        : item === 'Školička'
                        ? '/skolicka'
                        : item === 'Doplňkové služby'
                        ? '/doplnkove-sluzby'
                        : item === 'Kontakt'
                        ? '/kontakt'
                        : '/'
                    }
                    className="hover:text-yellow-300 transition duration-300 font-semibold px-2 py-2"
                  >
                    {item}
                    <span className="pointer-events-none absolute left-1/2 -translate-x-1/2 -bottom-0.5 h-[2px] w-0 bg-yellow-300 transition-all duration-300 group-hover:w-full" />
                  </Link>
                </li>
              )
            )}
          </ul>

          <div className="flex items-center md:hidden">
            <button onClick={toggleMenu} aria-label="Toggle Menu">
              {isMenuOpen ? <CloseIcon className="h-8 w-8" /> : <MenuIcon className="h-8 w-8" />}
            </button>
          </div>

          <div
            className={`md:hidden fixed top-[4.5rem] right-0 w-64 bg-amber-50 text-orange-700 shadow-md transform transition-transform duration-500 ${
              isMenuOpen ? 'translate-x-0' : 'translate-x-full'
            } rounded-xl overflow-hidden`}
          >
            <ul className="flex flex-col">
              {['O nás', 'Aktuality', 'Kempy', 'Ceník', 'Školička', 'Doplňkové služby', 'Kontakt'].map(
                (item, idx) => (
                  <li
                    key={item}
                    className="border-b border-orange-200"
                    style={{ animation: `fadeIn .3s ease ${(idx + 1) * 60}ms both` }}
                  >
                    <Link
                      to={
                        item === 'O nás'
                          ? '/o-nas'
                          : item === 'Aktuality'
                          ? '/aktuality'
                          : item === 'Kempy'
                          ? '/kempy'
                          : item === 'Ceník'
                          ? '/cenik'
                          : item === 'Školička'
                          ? '/skolicka'
                          : item === 'Doplňkové služby'
                          ? '/doplnkove-sluzby'
                          : item === 'Kontakt'
                          ? '/kontakt'
                          : '/'
                      }
                      className="block px-4 py-3 hover:bg-orange-200 transition duration-300 font-semibold"
                      onClick={() => setIsMenuOpen(false)}
                    >
                      {item}
                    </Link>
                  </li>
                )
              )}
            </ul>
          </div>
        </nav>
      </header>

      {/* MAIN */}
      <main className="pt-24 max-w-screen-xl mx-auto mb-16 px-4 sm:px-6 lg:px-8">
        <section className="relative bg-white rounded-2xl shadow-lg p-8 overflow-hidden">
          <header className="text-center relative z-[1] mb-8">
            <div className="flex items-center justify-center gap-2">
              <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-sm font-semibold bg-orange-50 text-orange-700 border border-orange-200">
                <DollarSign className="h-4 w-4" /> Ceník
              </span>
            </div>
            <h1 className="mt-2 text-4xl md:text-5xl font-extrabold text-orange-700">Ceník</h1>
            <p className="mt-3 text-lg text-gray-700 max-w-2xl mx-auto">
              Aktuální ceník pro sezony 2026 a 2027. Přepínejte mezi sekcemi a rychle najděte, co potřebujete.
            </p>
          </header>

          {/* TABS */}
          <div className="relative z-[1] mb-8 flex flex-wrap items-center justify-center gap-3">
            {TABS.map(({ key, label, icon }) => (
              <TabButton
                key={key}
                icon={icon}
                label={label}
                active={tab === key}
                onClick={() => setTab(key)}
              />
            ))}
          </div>

          {/* CONTENT */}
          <div className="relative z-[1]">
            {/* Pronájem haly (zima) */}
            {tab === 'pronajemZima' && (
              <>
                <h2 className="text-2xl font-extrabold text-gray-900 text-center mb-2">
                  Pronájem haly – zimní sezona 2026/2027
                </h2>
                <p className="text-center text-gray-600 mb-8">od 10. 10. 2026 do 9. 4. 2027</p>

                <h3 className="text-lg font-bold text-gray-900 mb-3">Běžné ceny</h3>
                <p className="text-sm text-gray-600 mb-3">Ceny jsou stejné pro všechny typy povrchů</p>
                <SimpleTable
                  headers={['Časové pásmo', 'Po–Pá', 'So–Ne']}
                  rows={[
                    ['07:00 – 14:00', '440 Kč / 1h', '440 Kč / 1h'],
                    ['14:00 – 21:00', '580 Kč / 1h', '440 Kč / 1h'],
                    ['21:00 – 23:00', '440 Kč / 1h', '440 Kč / 1h'],
                  ]}
                />

                <div className="mt-10">
                  <h3 className="text-lg font-bold text-gray-900 mb-3">Předplatné na celou zimní sezónu</h3>
                  <p className="text-gray-600 text-sm mb-3">Platí od 10. 10. 2026 do 9. 4. 2027</p>
                  <SimpleTable
                    headers={['Časové pásmo', 'Po–Pá', 'So–Ne']}
                    rows={[
                      ['07:00 – 14:00', '420 Kč / 1h', '420 Kč / 1h'],
                      ['14:00 – 21:00', '530 Kč / 1h', '420 Kč / 1h'],
                      ['21:00 – 23:00', '420 Kč / 1h', '420 Kč / 1h'],
                    ]}
                  />
                </div>
              </>
            )}

            {/* Tréninky – zima */}
            {tab === 'treninkyZima' && (
              <>
                <h2 className="text-2xl font-extrabold text-gray-900 text-center mb-2">Zimní sezona 2026/2027 – tréninky</h2>
                <p className="text-center text-gray-600 mb-6">od 10. 10. 2026 do 9. 4. 2027</p>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                  {[
                    { name: 'Individuál – 1 hráč + trenér', price: '850 Kč / 1h' },
                    { name: 'Dvojice – 2 hráči + trenér', price: '470 Kč / 1h' },
                    { name: 'Trojice – 3 hráči + trenér', price: '370 Kč / 1h' },
                    { name: 'Skupina – 4 hráči + trenér', price: '280 Kč / 1h' },
                    { name: 'Skupina / školička – více hráčů', price: '240 Kč / 1h' },
                  ].map((s) => (
                    <TrainingCard key={s.name} title={s.name} price={s.price} />
                  ))}
                </div>

                <div className="mt-8 p-5 bg-orange-50 rounded-2xl border border-orange-200 text-center">
                  <h3 className="text-lg font-extrabold text-gray-900">Tréninková sobota</h3>
                  <p className="text-gray-800 mt-1.5 text-sm">3hodinový sparingový trénink (9:00–12:00)</p>
                  <p className="text-2xl font-extrabold text-orange-600 mt-1.5">350 Kč</p>
                </div>
              </>
            )}

            {/* Pronájem kurtů – léto */}
            {tab === 'pronajemLeto' && (
              <>
                <h2 className="text-2xl font-extrabold text-gray-900 text-center mb-2">
                  Pronájem kurtů – letní sezona 2026/2027
                </h2>
                <p className="text-center text-gray-600 mb-8">5. 4. – 9. 10. 2026 a 5. 4. – 28. 6. 2027</p>

                <SimpleTable
                  headers={['Typ kurtu', 'Běžná cena', 'S předplatným na sezonu']}
                  rows={[
                    ['Antukové kurty', '200 Kč / 1h', '180 Kč / 1h'],
                    ['Kurty s umělou trávou', '200 Kč / 1h', '180 Kč / 1h'],
                    ['Večerní osvětlení (příplatek)', '+220 Kč / 1h', '+220 Kč / 1h'],
                  ]}
                />
              </>
            )}

            {/* Tréninky – léto */}
            {tab === 'treninkyLeto' && (
              <>
                <h2 className="text-2xl font-extrabold text-gray-900 text-center mb-2">Letní sezona 2026/2027 – tréninky</h2>
                <p className="text-center text-gray-600 mb-6">5. 4. – 9. 10. 2026 a 5. 4. – 28. 6. 2027</p>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                  {[
                    { name: 'Individuál – 1 hráč + trenér', price: '650 Kč / 1h' },
                    { name: 'Dvojice – 2 hráči + trenér', price: '370 Kč / 1h' },
                    { name: 'Trojice – 3 hráči + trenér', price: '270 Kč / 1h' },
                    { name: 'Skupina – 4 hráči + trenér', price: '210 Kč / 1h' },
                    { name: 'Skupina / školička – více hráčů', price: '200 Kč / 1h' },
                  ].map((s) => (
                    <TrainingCard key={s.name} title={s.name} price={s.price} />
                  ))}
                </div>

                <div className="mt-8 p-5 bg-orange-50 rounded-2xl border border-orange-200 text-center">
                  <h3 className="text-lg font-extrabold text-gray-900">Tréninková sobota</h3>
                  <p className="text-gray-800 mt-1.5 text-sm">3hodinový sparingový trénink (9:00–12:00)</p>
                  <p className="text-2xl font-extrabold text-orange-600 mt-1.5">350 Kč</p>
                </div>
              </>
            )}
          </div>

          {/* INFO BLOK (přesunuto pod obsah tabů) */}
          <div className="mt-10 p-6 bg-white rounded-2xl shadow-md border border-orange-200">
            <h3 className="text-2xl font-extrabold text-orange-600 mb-4">Důležité informace</h3>
            <p className="text-gray-800 mb-3">
              Cena je za jednu tréninkovou lekci a zahrnuje trenéra, pronájem kurtu, míče, pomůcky, DPH i pojištění.
            </p>
            <p className="text-gray-800 mb-3">
              Platba probíhá formou předplaceného kreditu – odečítají se pouze <strong>odehrané</strong> tréninky a
              systém umožňuje kombinovat různé počty hráčů i hodin.
            </p>
            <p className="text-gray-800 mb-3 font-semibold">Výhody našeho platebního systému při dodržení pravidel omluv:</p>
            <ul className="list-disc list-inside text-gray-800 space-y-1">
              <li>Neplatíte celou sezonu předem.</li>
              <li>Neúčtujeme prázdniny a svátky.</li>
              <li>Kredit doplňujete až po vyčerpání, posíláme elektronický přehled.</li>
              <li>Hradíte pouze reálně odehrané hodiny dle tarifu.</li>
              <li>Variabilita počtu tréninků i spoluhráčů.</li>
              <li>Trénink nepropadá při zrušení kvůli počasí.</li>
            </ul>
            <p className="text-gray-800 mt-3">
              Uvedené ceny TŠ platí pro děti trénující pravidelně a členy sokolské organizace.
              Nečlenové mají cenu o <strong>10 % vyšší</strong>.
            </p>
          </div>

          {/* CTA */}
          <div className="text-center mt-10">
            <Link
              to="/kontakt"
              className="inline-block bg-gradient-to-r from-orange-500 to-yellow-500 text-white px-6 py-3 rounded-full font-semibold hover:shadow-lg hover:scale-[1.02] transition"
            >
              Chci trénovat / pronajmout kurt
            </Link>
          </div>
        </section>
      </main>

      {/* FOOTER */}
      <footer className="bg-gray-800 text-white px-4 sm:px-6 lg:px-8">
        <div className="max-w-screen-xl mx-auto py-8 flex flex-col md:flex-row justify-between">
          <div className="mb-4 md:mb-0">
            <h3 className="text-2xl font-semibold mb-2">Tenis Čimice</h3>
            <p className="text-gray-400">
              Na Průhonu 812/3, 181 00 Praha 8 <br /> © 2026 Tenis Čimice
            </p>
          </div>
          <div>
            <h3 className="text-xl font-semibold mb-2">Kontaktujte nás</h3>
            <ul>
              <li className="flex items-center">
                <Mail className="mr-2" />
                <a href="mailto:kptennis@volny.cz" className="text-gray-400 hover:text-white">
                  kptenis@volny.cz
                </a>
              </li>
              <li className="flex items-center">
                <Phone className="mr-2" />
                <a href="tel:+420602354978" className="text-gray-400 hover:text-white">
                  +420 602 354 978
                </a>
              </li>
              <li className="flex items-center">
                <Phone className="mr-2" />
                <a href="tel:+420724265022" className="text-gray-400 hover:text-white">
                  +420 724 265 022
                </a>
              </li>
            </ul>
          </div>
        </div>
      </footer>

      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(4px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .pricing-table th,
        .pricing-table td {
          color: #1f2937;
          padding: 0.875rem 1.25rem;
          text-align: left;
          font-size: 0.9375rem;
          border-top: 1px solid #e5e7eb;
          vertical-align: middle;
        }
        .pricing-table th {
          font-weight: 600;
          white-space: nowrap;
        }
        .pricing-table thead tr {
          border-bottom: 1px solid #d1d5db;
        }
        .pricing-table tbody tr:first-child td {
          border-top: none;
        }
      `}</style>
    </div>
  );
};

export default Prising;
